import { CustomEventType } from "@/events/CustomEventRegistry";
import { CSSResultGroup, LitElement, TemplateResult, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";

export enum UserMessageBoxState {
	SUCCESS = 'success',
	WARNING = 'warning',
	ERROR = 'error',
}

export type UserMessageBoxDetail = {
	state: UserMessageBoxState;
	message: string;
	loading: boolean;
};

export function showUserMessage(detail: UserMessageBoxDetail): void {
	window.dispatchEvent(new CustomEvent<UserMessageBoxDetail>(CustomEventType.USER_MESSAGE, { detail }));
}

export function hideUserMessage(): void {
	window.dispatchEvent(new CustomEvent<null>(CustomEventType.USER_MESSAGE, { detail: null }));
}

@customElement("user-message-box")
export class UserMessageBox extends LitElement {
	static readonly styles: CSSResultGroup = css`
		:host {
			display: none;
			position: fixed;
			bottom: 24px;
			left: 50%;
			transform: translateX(-50%);
			z-index: 1000;
		}

		:host([visible]) {
			display: block;
		}

		.box {
			display: flex;
			align-items: center;
			gap: 12px;
			padding: 12px 20px;
			background: white;
			border: var(--border-default);
			border-radius: var(--border-radius);
			box-shadow: var(--shadow-default);
			font-family: var(--font-text);
			font-size: 1rem;
			color: black;
			animation: user-message-box-slide-up 0.3s ease-out;
		}

		:host([state="success"]) .box {
			border-color: var(--color-mint);
		}

		:host([state="warning"]) .box {
			border-color: var(--color-lemon);
		}

		:host([state="error"]) .box {
			border-color: var(--color-pink);
		}

		.spinner {
			flex-shrink: 0;
			width: 16px;
			height: 16px;
			border: var(--border-width) solid var(--color-sand);
			border-top-color: transparent;
			border-radius: 50%;
			animation: user-message-box-spin 0.8s linear infinite;
		}

		@keyframes user-message-box-spin {
			to { transform: rotate(360deg); }
		}

		@keyframes user-message-box-slide-up {
			from { transform: translateY(16px); opacity: 0; }
			to { transform: translateY(0); opacity: 1; }
		}
	`;

	@property({ type: String, reflect: true }) state: UserMessageBoxState = UserMessageBoxState.SUCCESS;
	@property({ type: String }) message = '';
	@property({ type: Boolean }) loading = false;
	@property({ type: Boolean, reflect: true }) visible = false;

	connectedCallback(): void {
		super.connectedCallback();
		window.addEventListener(CustomEventType.USER_MESSAGE, this._handleUserMessageEvent);
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();
		window.removeEventListener(CustomEventType.USER_MESSAGE, this._handleUserMessageEvent);
	}

	render(): TemplateResult {
		return html`
			<div class="box" role="status" aria-live="polite">
				${this.loading ? html`<span class="spinner"></span>` : ''}
				<span>${this.message}</span>
			</div>
		`;
	}

	private readonly _handleUserMessageEvent = (event: Event): void => {
		const detail = (event as CustomEvent<UserMessageBoxDetail | null>).detail;
		if (detail === null) {
			this.visible = false;
			return;
		}
		this.state = detail.state;
		this.message = detail.message;
		this.loading = detail.loading;
		this.visible = true;
	};
}

export default UserMessageBox;
